import { useState, useRef, useCallback, useEffect } from 'react'
import Player from 'react-native-video'

interface UseGongPreviewReturn {
  playingGongId: string | null
  isLoading: boolean
  playGong: (gongId: string, audioUrl: string) => void
  stopGong: () => void
  playerProps: {
    source: { uri: string } | null
    paused: boolean
    onLoad: () => void
    onLoadStart: () => void
    onProgress: (data: any) => void
    onEnd: () => void
    onError: (error: any) => void
    setPlayerRef: (ref: Player | null) => void
  }
}

export function useGongPreview(): UseGongPreviewReturn {
  const [playingGongId, setPlayingGongId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null)
  const [paused, setPaused] = useState(true)
  const playerRef = useRef<Player | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const stopGong = useCallback(() => {
    setPaused(true)
    setCurrentAudioUrl(null)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setPlayingGongId(null)
    setIsLoading(false)
  }, [])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopGong()
    }
  }, [stopGong])

  const playGong = useCallback(
    (gongId: string, audioUrl: string) => {
      // Stop any currently playing gong
      stopGong()

      // If clicking the same gong that was playing, just stop
      if (playingGongId === gongId) {
        return
      }

      setIsLoading(true)
      setPlayingGongId(gongId)
      setCurrentAudioUrl(audioUrl)

      // Set timeout to prevent loading state from hanging
      timeoutRef.current = setTimeout(() => {
        console.error('Gong loading timeout')
        stopGong()
      }, 10000) // 10 second timeout

      // Audio will start playing when the Player component loads
      setPaused(false)
    },
    [playingGongId, stopGong],
  )

  const handleLoadStart = useCallback(() => {
    // This is called when loading starts
    setIsLoading(true)
  }, [])

  const handleLoad = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsLoading(false)
  }, [])

  const handleProgress = useCallback(
    (data: any) => {
      // When we receive the first progress event, the audio is playing
      // This is especially useful for web where onLoad might not fire consistently
      if (isLoading && data.currentTime > 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setIsLoading(false)
      }
    },
    [isLoading],
  )

  const handleEnd = useCallback(() => {
    stopGong()
  }, [stopGong])

  const handleError = useCallback(
    (error: any) => {
      console.error('Audio error:', error)
      stopGong()
    },
    [stopGong],
  )

  const setPlayerRef = useCallback((ref: Player | null) => {
    playerRef.current = ref
  }, [])

  return {
    playingGongId,
    isLoading,
    playGong,
    stopGong,
    playerProps: {
      source: currentAudioUrl ? { uri: currentAudioUrl } : null,
      paused,
      onLoad: handleLoad,
      onLoadStart: handleLoadStart,
      onProgress: handleProgress,
      onEnd: handleEnd,
      onError: handleError,
      setPlayerRef,
    },
  }
}
